'use client';

import type { BlogGridProps } from '@routeroyal/theme-core';
import { AllysBlogCard } from './AllysBlogCard';

/**
 * AllysBlogGrid - Allys-themed blog grid
 * Displays blog articles in a grid with Allys styling
 */
export function AllysBlogGrid({
  articles,
  variant = 'grid',
  columns = 3,
  emptyMessage = 'No articles found',
  locale = 'en',
  className = '',
}: BlogGridProps) {
  if (articles.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
          {emptyMessage}
        </p>
      </div>
    );
  }

  const isList = variant === 'list';

  const gridClasses = isList
    ? 'space-y-6'
    : `grid gap-6 ${
        columns === 1
          ? 'grid-cols-1'
          : columns === 2
          ? 'grid-cols-1 md:grid-cols-2'
          : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      }`;

  // Featured article (first one) gets special treatment in grid view
  const featuredArticle = !isList && articles.length > 0 ? articles[0] : null;
  const regularArticles = !isList && articles.length > 0 ? articles.slice(1) : articles;

  return (
    <div className={`[font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${className}`}>
      {/* Section heading for accessibility - visually hidden */}
      <h2 className="sr-only">Latest Articles</h2>
      
      {/* Featured Article (only in grid view) */}
      {featuredArticle && (
        <div className="mb-8">
          <AllysBlogCard
            article={featuredArticle}
            variant="featured"
            showExcerpt={true}
            showCategory={true}
            showAuthor={true}
            locale={locale}
          />
        </div>
      )}

      {/* Regular Articles Grid */}
      {regularArticles.length > 0 && (
        <div className={gridClasses}>
          {regularArticles.map((article) => (
            <AllysBlogCard
              key={article.id}
              article={article}
              variant={isList ? 'compact' : 'default'}
              showExcerpt={!isList}
              showCategory={true}
              showAuthor={true}
              locale={locale}
            />
          ))}
        </div>
      )}
    </div>
  );
}

